import React, {
  useEffect, useMemo, useState,
} from "react"
import BadgeBuilder from "components/BadgeBuilder/BadgeBuilder"
import {
  EditorActions, EditorHeader, EditorName, EditorTitle,
} from "components/CanvasEditor/EditorHeader"
import useUnsavedChangesGuard from "components/CanvasEditor/useUnsavedChangesGuard"
import Modal from "components/dialogs/Modal"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import { useHistory, useParams } from "react-router-dom"
import * as api from "api/badges"
import { useBadge } from "queries/badges"
import Spinner from "components/elements/Spinner"
import { useMutation, useQueryClient } from "react-query"
import {
  BadgeTemplate,
} from "sharedTypes/badge"
import SAMPLE_GUEST_DATA from "utilities/sampleGuestData"
import { SAMPLE_EVENT_DATA } from "components/BadgeBuilder/utils/sampleData"
import { validateBadgeTemplate } from "components/BadgeBuilder/utils/badgeValidation"
import { eventBadgesPath } from "utilities/routes"
import { ModalName } from "sharedTypes"
import useModal from "services/useModal"
import { addBadge, updateBadge } from "context/actions"

const BadgeTemplateEdit = () => {
  const history = useHistory()
  const showModal = useModal()
  const queryClient = useQueryClient()
  const { templateId, eventId } = useParams<{
    templateId: string
    eventId: string
  }>()

  const { isLoading, data: badgeData } = useBadge(eventId, templateId)

  const [currentTemplate, setCurrentTemplate] = useState<
    BadgeTemplate | undefined
  >()
  const [originalTemplate, setOriginalTemplate] = useState<
    BadgeTemplate | undefined
  >()

  // Worked out once per change, not on every render
  const hasPendingChanges = useMemo(() => {
    if (!currentTemplate || !originalTemplate || currentTemplate === originalTemplate) return false

    const { updatedAt: currentUpdated, ...currentWithoutUpdatedAt } = currentTemplate
    const { updatedAt: originalUpdated, ...originalWithoutUpdatedAt } = originalTemplate

    return JSON.stringify(currentWithoutUpdatedAt) !== JSON.stringify(originalWithoutUpdatedAt)
  }, [currentTemplate, originalTemplate])

  useEffect(() => {
    if (badgeData && !isLoading) {
      // Loaded from backend via React Query
      setOriginalTemplate(badgeData)
      setCurrentTemplate(badgeData)
    }
  }, [badgeData, isLoading])

  const releaseGuard = useUnsavedChangesGuard(hasPendingChanges)

  const saveBadge = useMutation(
    (template: BadgeTemplate) => {
      const badgePayload: Partial<BadgeTemplate> = {
        name: template.name,
        widthMm: template.widthMm,
        heightMm: template.heightMm,
        printerModel: template.printerModel,
        printerMedia: template.printerMedia,
        printerOrientation: template.printerOrientation,
        badgeData: {
          elements: template.badgeData.elements,
        },
      }

      if (template.id && !template.id.startsWith("new-")) {
        return api.putBadge(eventId, template.id, badgePayload).then(({ data }) => data)
      }

      return api.postBadge(eventId, badgePayload).then(({ data }) => data)
    },
    {
      onSuccess: (savedTemplate) => {
        setCurrentTemplate({ ...savedTemplate })
        setOriginalTemplate({ ...savedTemplate })
        // Determine if this was a create or update operation
        const isUpdate = originalTemplate?.id && !originalTemplate.id.startsWith("new-")
        if (isUpdate) {
          updateBadge(queryClient, eventId, savedTemplate)
        } else {
          addBadge(queryClient, eventId, savedTemplate)
        }
        // Release the leave warning before navigating away
        releaseGuard()
        history.push(eventBadgesPath(eventId))
      },
    },
  )

  const handleSaveTemplate = () => {
    if (!currentTemplate) return

    const issues = validateBadgeTemplate(currentTemplate)
    if (issues.length > 0) {
      showModal(ModalName.BadgeTemplateSaveValidationWarning, {
        issues,
        onSaveWithWarnings: () => saveBadge.mutate(currentTemplate),
      })

      return
    }

    saveBadge.mutate(currentTemplate)
  }

  const handleCancel = () => {
    history.push(eventBadgesPath(eventId))
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Modal
      show
      size="full"
      hide={() => { }}
      header={(
        <EditorHeader>
          <EditorTitle dirty={hasPendingChanges}>
            {currentTemplate ? currentTemplate.name : "Edit Badge Template"}
          </EditorTitle>
          <EditorName>Badge Builder</EditorName>
          <EditorActions>
            <Link onClick={handleCancel} color="gray.500" fontWeight="bold">
              Cancel
            </Link>
            <Link
              onClick={handleSaveTemplate}
              color="blue.300"
              fontWeight="bold"
              noOfLines={1}
            >
              Save Template
              <Icon icon="cancel-circle" ml={2} />
            </Link>
          </EditorActions>
        </EditorHeader>
      )}
      noPadding
    >
      {originalTemplate && originalTemplate.badgeData ? (
        <BadgeBuilder
          key={originalTemplate.id}
          template={originalTemplate}
          onTemplateChange={setCurrentTemplate}
          guestData={SAMPLE_GUEST_DATA}
          eventData={SAMPLE_EVENT_DATA}
        />
      ) : (
        <Spinner />
      )}
    </Modal>
  )
}

export default BadgeTemplateEdit
